import { IconInstant } from "@/components/design-system/icons/IconInstant";
import { IconRecording } from "@/components/design-system/icons/IconRecording";
import { IconSendMessages } from "@/components/design-system/icons/IconSendMessages";
import { IconUnbox } from "@/components/design-system/icons/IconUnbox";
import { SectionHeader } from "@/components/design-system/SectionHeader/SectionHeader";

import React from "react";

type UnlockCard = {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
};

const unlockCards: readonly UnlockCard[] = [
  {
    icon: <IconInstant className="text-primary-500 h-16 w-16" />,
    title: "Power better decisions",
    description:
      "Help your users answer real financial questions with personalized, contextual guidance at the moment it matters most.",
  },
  {
    icon: <IconUnbox className="text-primary-500 h-16 w-16" />,
    title: "Uncover hidden opportunity",
    description:
      "Surface unmet needs, overlooked assets, and untapped potential through natural conversation.",
  },
  {
    icon: <IconRecording className="text-primary-500 h-16 w-16" />,
    title: "Enhance human interactions",
    description:
      "Context-rich handoffs to advisors, coaches, or support teams at exactly the right moment.",
  },
  {
    icon: <IconSendMessages className="text-primary-500 h-16 w-16" />,
    title: "Deepen the relationship",
    description:
      "Every interaction builds understanding — creating the foundation for trusted, long-term engagement.",
  },
] as const;

export const UnlockGrowthSection: React.FC = () => {
  return (
    <section id="unlock-growth" className="bg-primary-700 w-full">
      <div className="section-inset pt-10 text-center md:pt-20">
        <SectionHeader
          label="Unlock Growth"
          heading="Every conversation is an opportunity."
          description="Your users have everyday financial questions. Bravo keeps those conversations inside your ecosystem while surfacing opportunities across your entire user base."
          theme="dark"
          className="mb-5 md:mb-8"
        />
      </div>

      <div className="section-inset flex flex-col gap-6 pb-10 md:flex-row md:justify-center md:pb-20">
        {unlockCards.map((card) => (
          <div
            key={card.title}
            className="group relative flex flex-col gap-4 rounded-2xl bg-white p-6 transition-all duration-300 md:w-66 md:hover:z-10 md:hover:scale-[1.2] md:hover:shadow-[-6px_-6px_40px_0px_rgba(182,98,166,0.7),6px_6px_40px_0px_rgba(242,185,75,0.7)]"
          >
            <div className="bg-primary-50 md:group-hover:bg-accent-yellow/20 flex size-20 shrink-0 items-center justify-center rounded-[20px] transition-all duration-300 md:group-hover:rounded-3xl">
              {card.icon}
            </div>

            <h3 className="subtitle-a-bold text-black">
              {card.title.split(" ").map((word, i, arr) =>
                i === arr.length - 1 ? (
                  <span
                    key={i}
                    className="md:group-hover:text-accent-yellow transition-colors duration-300"
                  >
                    {word}
                  </span>
                ) : (
                  word + " "
                )
              )}
            </h3>
            <p className="body-b-regular text-black">{card.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
